import React from 'react';
import { ArrowRight, Calendar, Users, GraduationCap, BookOpen, Heart, Sparkles, CheckCircle2, Star } from 'lucide-react';
import Footer from '../components/Footer';

const Home = () => {

  const announcements = [
    {
      id: 1,
      title: "New 3K Classroom - Spanish Language Program",
      date: "Current Year",
      image: "https://goldprekd9.com/wp-content/uploads/2023/05/New-3K-Classroom-Spa-Flyer_page-0001.jpg"
    },
    {
      id: 2,
      title: "New 3K Classroom Opening",
      date: "Current Year",
      image: "https://goldprekd9.com/wp-content/uploads/2023/05/New-3K-Classroom-Flyer_page-0001.jpg"
    },
    {
      id: 3,
      title: "NYC Kids Rise Visits P.S. 85!",
      date: "January 12, 2024",
      image: "https://csd10bronx.org/wp-content/uploads/2024/01/NYC-Kids-Rise-Visits-P.S.-85-1-960x720.webp"
    }
  ];

  return (
    <div className="min-h-screen bg-[#FFFBEB] font-sans selection:bg-[#febc04] selection:text-[#123f1e]">
      {/* Hero Section — one-screen video with a lower-third title band */}
      {/* bg-[#123f1e]: solid fallback behind the video; users get it too while
          the video loads. */}
      <section id="home" className="relative flex h-[100svh] min-h-[560px] w-full flex-col overflow-hidden bg-[#123f1e]">
        {/* Video Background — decorative, looped, muted; surrounding text carries all semantic content. */}
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src="/assets/videos/garden-of-learning.mp4"
          autoPlay
          muted
          loop
          playsInline
          aria-hidden="true"
          tabIndex={-1}
        />
        {/* Gentle top/bottom vignette only. No text sits on the footage any
            more, so the overlay no longer has to carry contrast; the top stop
            just keeps the area behind the floating nav calm. */}
        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-b from-[#123f1e]/40 via-transparent to-[#123f1e]/40"></div>

        {/* Lower-third band — all hero copy sits on a slim, SOLID dark-green
            strip pinned to the bottom of the hero, so the video stays
            unobstructed above it and every contrast ratio is decidable
            (axe cannot sample a <video> frame). On #123f1e: white 11.96:1,
            #febc04 7.07:1. Keep this background opaque. */}
        <div className="relative z-10 mt-auto w-full border-t-2 border-[#febc04] bg-[#123f1e]">
          {/* The fixed language picker sits bottom-right below 2xl: extra bottom padding on phones and
              right padding on desktop keep it off the buttons. */}
          <div className="mx-auto flex max-w-[1400px] flex-col gap-3 px-6 pt-3.5 pb-[4.75rem] sm:pb-3.5 md:px-10 md:py-4 lg:flex-row lg:items-center lg:justify-between lg:gap-8 lg:pr-48 2xl:pr-10">
            <div className="min-w-0">
              <h1 className="text-xl md:text-2xl lg:text-3xl font-black text-white leading-tight tracking-tight">
                Where Young{' '}
                <span className="whitespace-nowrap text-[#febc04]">Minds Bloom</span>
              </h1>
              {/* The full intro stays in the accessibility tree; visually the
                  band carries only the motto to keep it slim. */}
              <p className="sr-only">
                Fostering a culturally responsive environment that celebrates diversity, equity, and excellence in early childhood education.
              </p>
              <p className="mt-0.5 text-xs md:text-sm font-semibold text-[#febc04]">At GoLD, we blossom!</p>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:gap-3 lg:shrink-0">
              <a
                href="#programs"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#febc04] px-3 py-2.5 text-xs font-black uppercase text-center tracking-[0.06em] text-[#123f1e] transition-colors duration-200 hover:bg-white sm:px-4 sm:tracking-[0.14em]"
              >
                <span>Explore Programs</span>
                <span className="hidden sm:inline-flex">
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200 motion-reduce:transform-none" />
                </span>
              </a>
              <a
                href="https://docs.google.com/forms/d/e/1FAIpQLSdqpqYqusvTY9VIdVAGoBZVVVSr-PSPJhY937uPOTPFPsjbbg/viewform"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white bg-white px-3 py-2 text-xs font-black uppercase text-center tracking-[0.06em] text-[#123f1e] transition-colors duration-200 hover:bg-[#febc04] hover:border-[#febc04] sm:px-4 sm:tracking-[0.14em]"
              >
                Join Waitlist
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Section - Cards like sticky notes/flashcards */}
      <section id="programs" className="py-24 sm:py-32 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(#123f1e_1px,transparent_1px)] [background-size:20px_20px] opacity-[0.03]"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16 sm:mb-20 text-center mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#e8f5e9] rounded-full mb-6 border border-[#123f1e]/10">
              <Sparkles className="w-5 h-5 text-[#123f1e]" />
              <span className="text-sm font-bold text-[#123f1e]">Our Programs</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-black text-[#123f1e] mt-4 leading-[1.15]">
              Building Foundations
              <br />
              <span className="text-[#7d5800] inline-block transform -rotate-1">For Tomorrow</span>
            </h2>
            <p className="text-xl text-[#123f1e]/80 mt-6 leading-relaxed font-medium">
              Comprehensive Pre-K education programs designed to nurture each child's unique journey
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <ProgramCardSmall
              icon={<GraduationCap className="w-8 h-8" />}
              title="Pre-K Program"
              description="Comprehensive Pre-K curriculum"
              href="#special-ed"
              color="bg-[#FFF8E1]"
              borderColor="border-[#febc04]"
              rotate="rotate-1"
            />

            <ProgramCardSmall
              icon={<BookOpen className="w-8 h-8" />}
              title="3-K Program"
              description="Early childhood education for 3-year-olds"
              href="#programs"
              color="bg-[#F1F8E9]"
              borderColor="border-[#123f1e]"
              rotate="-rotate-1"
            />

            <ProgramCardSmall
              icon={<Heart className="w-8 h-8" />}
              title="Special Education"
              description="Comprehensive support services"
              href="#special-ed"
              color="bg-[#FFF0F5]"
              borderColor="border-pink-300"
              rotate="rotate-1"
            />

            <ProgramCardSmall
              icon={<Users className="w-8 h-8" />}
              title="Family Engagement"
              description="Building strong partnerships"
              href="#family"
              color="bg-[#E3F2FD]"
              borderColor="border-blue-300"
              rotate="-rotate-1"
            />
          </div>
        </div>
      </section>

      {/* Special Education - Friendly & Supportive */}
      <section className="py-24 sm:py-32 bg-[#123f1e] text-white relative overflow-hidden" id="special-ed">
        {/* Doodle Pattern Background */}
        <div className="absolute inset-0 opacity-5">
           <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
             <pattern id="doodle-pattern" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
               <circle cx="2" cy="2" r="1" fill="currentColor" />
               <path d="M10 10 L15 15" stroke="currentColor" strokeWidth="0.5" />
               <rect x="15" y="5" width="2" height="2" fill="currentColor" />
             </pattern>
             <rect width="100%" height="100%" fill="url(#doodle-pattern)" />
           </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-3 space-y-8">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#febc04]/10 rounded-full mb-4 border border-[#febc04]/30">
                  <Heart className="w-5 h-5 text-[#febc04]" fill="currentColor" />
                  <span className="text-sm font-bold text-[#febc04]">Special Education</span>
                </div>
                <h2 className="text-4xl sm:text-5xl font-black mt-4 leading-[1.15]">
                  Every Child
                  <br />
                  <span className="text-[#febc04]">Deserves Excellence</span>
                </h2>
              </div>

              <p className="text-xl text-white/90 leading-relaxed font-medium">
                Comprehensive special education services tailored to support each student's unique learning journey.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                <ServiceBadge text="6:1:2, 8:1:2 & 12:1:1 Ratios" />
                <ServiceBadge text="Occupational Therapy" />
                <ServiceBadge text="Speech Therapy" />
                <ServiceBadge text="Physical Therapy" />
                <ServiceBadge text="CPSE Services" />
                <ServiceBadge text="Individualized Plans" />
              </div>

              <a
                href="/contact"
                aria-label="Learn more about Special Education programs"
                className="inline-flex items-center gap-2.5 px-8 py-4 bg-[#febc04] text-[#123f1e] font-black rounded-full border-b-4 border-[#d49b00] hover:border-b-0 hover:translate-y-1 transition-all duration-200"
              >
                Learn More
                <ArrowRight className="w-6 h-6" />
              </a>
            </div>

            <div className="lg:col-span-2">
              <div className="space-y-6">
                <div className="mb-6">
                  <img
                    src="/assets/images/gallery/District9PreK-9.jpg"
                    alt="GoLD Pre-K students participating in classroom activities"
                    className="w-full h-64 object-cover rounded-3xl shadow-xl"
                  />
                </div>
                <StatBox number="89%" label="Improved Attendance" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Family Engagement - Community Focused */}
      <section className="py-24 sm:py-32 bg-[#FFFBEB] relative overflow-hidden" id="family">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-3xl mx-auto mb-16 sm:mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full mb-6 border-2 border-[#febc04]/20 shadow-sm">
              <Users className="w-5 h-5 text-[#febc04]" />
              <span className="text-sm font-bold text-[#123f1e]">Family Engagement</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-black text-[#123f1e] mt-4 leading-[1.15]">
              Stronger
              <br />
              <span className="text-[#7d5800] underline decoration-wavy decoration-[#123f1e]/30 underline-offset-4">Together</span>
            </h2>
            <p className="text-xl text-[#123f1e]/80 mt-6 leading-relaxed font-medium">
              Building partnerships between families, educators, and community
            </p>
          </div>

          <div className="mb-12">
            <img
              src="/assets/images/gallery/District9PreK-170.jpg"
              alt="Families and students participating in school engagement activities"
              className="w-full max-w-4xl mx-auto h-80 object-cover object-top rounded-3xl shadow-xl"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <FeatureIconCard
              icon={<Calendar className="w-12 h-12" />}
              title="Monthly Family Days"
              description="Engaging activities that bring families into the learning process"
              color="bg-[#E3F2FD]"
              accent="#2196F3"
            />
            <FeatureIconCard
              icon={<Sparkles className="w-12 h-12" />}
              title="Cultural Celebrations"
              description="Honoring the diversity of our school community"
              color="bg-[#F3E5F5]"
              accent="#9C27B0"
            />
            <FeatureIconCard
              icon={<Heart className="w-12 h-12" />}
              title="Parent Workshops"
              description="Supporting families with resources and education"
              color="bg-[#FCE4EC]"
              accent="#E91E63"
            />
          </div>
        </div>
      </section>

      {/* Events - Calendar Style */}
      <section id="events" className="py-24 sm:py-32 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl mb-16 sm:mb-20 text-center mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#e8f5e9] rounded-full mb-6 border border-[#123f1e]/10">
              <Calendar className="w-5 h-5 text-[#123f1e]" />
              <span className="text-sm font-bold text-[#123f1e]">Events</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-black text-[#123f1e] mt-4 leading-[1.15]">
              Stay
              <span className="text-[#7d5800] px-2">Connected</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <EventCardModern
              title="School Calendar"
              description="Important dates and school events"
              href="/calendar"
              ariaLabel="View the full school calendar — important dates and school events"
              icon={<Calendar className="w-8 h-8 text-white" />}
              color="bg-[#febc04]"
            />
            <EventCardModern
              title="Family Events"
              description="Monthly engagement activities"
              href="/calendar#family-events"
              ariaLabel="See family events on the school calendar — monthly engagement activities"
              icon={<Users className="w-8 h-8 text-white" />}
              color="bg-[#123f1e]"
            />
            <EventCardModern
              title="Cultural Days"
              description="Celebrating our diverse community"
              href="/calendar#cultural-days"
              ariaLabel="See cultural days on the school calendar — celebrating our diverse community"
              icon={<Star className="w-8 h-8 text-white" />}
              color="bg-[#d49b00]"
            />
          </div>
        </div>
      </section>

      {/* Announcements - Social Media Grid */}
      <section id="announcements" className="py-24 sm:py-32 bg-[#fafaf9] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl mb-16 sm:mb-20 text-center mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#febc04]/10 rounded-full mb-6">
              <Sparkles className="w-5 h-5 text-[#febc04]" />
              <span className="text-sm font-bold text-[#123f1e]">Latest News</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-black text-[#123f1e] mt-4 leading-[1.15]">
              What's
              <br />
              <span className="text-[#7d5800]">Happening</span>
            </h2>
            <p className="text-xl text-[#123f1e]/80 mt-6 leading-relaxed font-medium">
              Stay connected with us on social media for the latest updates
            </p>
          </div>

          {/* Social Links */}
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-16">
            <SocialCard
              href="https://www.instagram.com/goldprekd9/?hl=en"
              icon={
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              }
              name="Instagram"
              handle="@goldprekd9"
              bgClass="bg-gradient-to-br from-purple-600 to-pink-600"
            />

            <SocialCard
              href="https://x.com/goldprekd9?lang=en"
              icon={
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              }
              name="Twitter (X)"
              handle="@goldprekd9"
              bgClass="bg-black"
            />

            <SocialCard
              href="https://apps.apple.com/us/app/gold-pre-k/id6448789658"
              icon={
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
              }
              name="School App"
              handle="Download on iOS"
              bgClass="bg-[#007AFF]"
            />
          </div>

          {/* News Cards - Playful Polaroid Style */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {announcements.map((announcement, index) => (
              <article
                key={announcement.id}
                className={`group bg-white p-3 pb-6 rounded-sm shadow-md hover:shadow-xl transition-all duration-300 transform ${index % 2 === 0 ? 'rotate-1 hover:rotate-0' : '-rotate-1 hover:rotate-0'}`}
              >
                <div className="relative h-64 overflow-hidden bg-gray-100 mb-4">
                  <img
                    src={announcement.image}
                    alt={announcement.title}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="px-3 py-1 bg-[#febc04] text-[#123f1e] text-xs font-black uppercase tracking-wider rounded-sm shadow-sm">
                      NEW
                    </span>
                  </div>
                </div>
                <div className="px-2">
                  <p className="text-xs font-bold text-[#123f1e]/80 mb-2 uppercase tracking-wider">{announcement.date}</p>
                  <h3 className="text-lg font-bold text-[#123f1e] mb-4 line-clamp-2 leading-snug">
                    {announcement.title}
                  </h3>
                  <a
                    href="#announcements"
                    aria-label={`Read more about ${announcement.title}`}
                    className="inline-flex items-center gap-2 text-[#123f1e] font-bold text-sm hover:text-[#7d5800] transition-colors duration-200"
                  >
                    Read More
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Playful Component Variations

const ProgramCardSmall = ({ icon, title, description, href, color, borderColor, rotate }) => (
  <a
    href={href}
    aria-label={`Explore ${title}`}
    className={`group relative flex flex-col h-full ${color} p-8 rounded-[2rem] border-4 ${borderColor} shadow-[4px_4px_0px_0px_rgba(0,0,0,0.1)] hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,0.1)] hover:-translate-y-1 transition-all duration-300 transform ${rotate} hover:rotate-0`}
  >
    <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center text-[#123f1e] mb-6 shadow-sm border-2 border-[#123f1e]/5 group-hover:scale-110 transition-transform duration-300">
      {icon}
    </div>
    <h3 className="text-xl font-black text-[#123f1e] mb-3">{title}</h3>
    <p className="text-[#123f1e]/80 text-sm leading-relaxed font-medium mb-6 flex-grow">{description}</p>
    <div className="flex items-center gap-2 text-[#123f1e] font-bold text-sm group-hover:gap-3 transition-all duration-200">
      <span>Explore</span>
      <ArrowRight className="w-5 h-5" />
    </div>
  </a>
);

const ServiceBadge = ({ text }) => (
  <div className="flex items-center gap-3 px-5 py-4 bg-white/[0.1] rounded-2xl backdrop-blur-sm border border-white/20 shadow-sm hover:bg-white/[0.2] transition-all duration-200">
    <CheckCircle2 className="w-6 h-6 text-[#febc04] flex-shrink-0" />
    <span className="font-bold text-sm tracking-wide">{text}</span>
  </div>
);

const StatBox = ({ number, label }) => (
  <div className="bg-white/[0.1] backdrop-blur-sm rounded-[2.5rem] p-10 border border-white/20 shadow-lg hover:bg-white/[0.15] transition-all duration-300 text-center">
    <div className="text-6xl font-black text-[#febc04] mb-3 drop-shadow-sm">{number}</div>
    <div className="text-white font-bold text-xl">{label}</div>
  </div>
);

const FeatureIconCard = ({ icon, title, description, color, accent }) => (
  <div className={`h-full ${color} p-8 rounded-[2rem] border-2 border-transparent hover:border-${accent} transition-all duration-300 text-center`}>
    <div className="inline-flex p-4 bg-white rounded-full text-[#123f1e] mb-6 shadow-sm mx-auto">
      {icon}
    </div>
    <h3 className="text-xl font-black text-[#123f1e] mb-3">{title}</h3>
    <p className="text-[#123f1e]/85 leading-relaxed font-medium">{description}</p>
  </div>
);

const EventCardModern = ({ title, description, href, ariaLabel, icon, color }) => (
  <a
    href={href}
    aria-label={ariaLabel || `View ${title}`}
    className="group relative flex flex-col h-full bg-white rounded-3xl overflow-hidden border-2 border-gray-100 hover:border-[#febc04] hover:shadow-xl transition-all duration-300"
  >
    <div className={`${color} h-24 flex items-center justify-center relative overflow-hidden`}>
      <div className="absolute inset-0 bg-black/5"></div>
      {icon}
    </div>
    <div className="p-8 flex-grow flex flex-col">
      <h3 className="text-xl font-black text-[#123f1e] mb-3">{title}</h3>
      <p className="text-[#123f1e]/80 mb-6 leading-relaxed font-medium flex-grow">{description}</p>
      <div className="flex items-center gap-2 text-[#7d5800] font-bold group-hover:gap-3 transition-all duration-200 mt-auto">
        <span>View Details</span>
        <ArrowRight className="w-5 h-5" />
      </div>
    </div>
  </a>
);

const SocialCard = ({ href, icon, name, handle, bgClass }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="group bg-white p-8 rounded-[2rem] border-2 border-gray-100 hover:border-[#febc04] hover:shadow-[0_8px_20px_-5px_rgba(0,0,0,0.1)] transition-all duration-200 text-center"
  >
    <div className={`w-16 h-16 ${bgClass} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-md group-hover:scale-110 transition-transform duration-300`}>
      {icon}
    </div>
    <h3 className="text-lg font-black text-[#123f1e] mb-1.5">{name}</h3>
    <p className="text-gray-500 text-sm font-bold">{handle}</p>
  </a>
);

export default Home;
